import { Vector3 } from 'three'
import { SplineType } from '../core/enums'
import { DreamteckSpline } from '../core/spline/dreamteckSpline'

/**
 * A throwaway `SplineComputer` the game builds at runtime, sampled into a
 * polyline the follower drives.
 *
 * Both of the original's disposable splines are built this way — the entry
 * curve out of the lot (`SplineController.CreateEntrySpline`) and the exit
 * curve off the end of a run (`BusSplineFollower.CreateExitSpline`). They agree
 * on every structural detail: a `GameObject` with a fresh `SplineComputer`,
 * `set_type` immediately after `AddComponent`, `sampleRate` copied from the
 * main spline, `updateMode` forced to 4, then `SetPoints` + `RebuildImmediate`.
 *
 * The type is **Catmull-Rom** — see `entryPath.ts` for the evidence, which
 * applies equally here since both call sites are byte-for-byte the same shape.
 */

const SPLINE_TYPE = SplineType.CatmullRom

/** `SplineComputer.sampleRate`, copied from the main spline. Dreamteck's default. */
export const PATH_SAMPLE_RATE = 10

export class SampledPath {
  /** Densely sampled curve, Unity space. */
  protected readonly points: Vector3[] = []
  protected readonly cumulative: number[] = []
  /** The control points before smoothing, for debug drawing. */
  readonly controlPoints: readonly Vector3[]

  constructor(controlPoints: Vector3[]) {
    this.controlPoints = controlPoints.map((p) => p.clone())

    if (controlPoints.length < 2) {
      for (const p of controlPoints) this.points.push(p.clone())
      if (this.points.length === 0) this.points.push(new Vector3())
    } else {
      // `SetPoints` + `RebuildImmediate`: the control points are knots of a
      // Catmull-Rom, not the curve itself.
      const curve = new DreamteckSpline(
        controlPoints.map((p) => p.clone()),
        SPLINE_TYPE,
        false,
        PATH_SAMPLE_RATE,
      )
      const steps = curve.segmentCount * PATH_SAMPLE_RATE
      for (let i = 0; i <= steps; i++) {
        this.points.push(curve.evaluatePosition(i / steps, new Vector3()))
      }
    }

    this.cumulative.push(0)
    for (let i = 1; i < this.points.length; i++) {
      this.cumulative.push(this.cumulative[i - 1] + this.points[i].distanceTo(this.points[i - 1]))
    }
  }

  get length(): number {
    return this.cumulative[this.cumulative.length - 1] ?? 0
  }

  /** The curve's own samples, Unity space. For debug drawing. */
  get samplePoints(): readonly Vector3[] {
    return this.points
  }

  /** Sample index a control point landed on — Catmull-Rom passes through its knots. */
  protected sampleIndexOfControlPoint(index: number): number {
    if (this.controlPoints.length < 2) return Math.min(index, this.points.length - 1)
    return Math.min(index * PATH_SAMPLE_RATE, this.points.length - 1)
  }

  private locate(distance: number): { i: number; t: number } {
    const d = Math.max(0, Math.min(this.length, distance))
    let lo = 0
    let hi = this.cumulative.length - 1
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1
      if (this.cumulative[mid] <= d) lo = mid
      else hi = mid
    }
    const span = this.cumulative[hi] - this.cumulative[lo]
    return { i: lo, t: span > 1e-6 ? (d - this.cumulative[lo]) / span : 0 }
  }

  positionAt(distance: number, out = new Vector3()): Vector3 {
    const { i, t } = this.locate(distance)
    const next = this.points[Math.min(i + 1, this.points.length - 1)]
    return out.copy(this.points[i]).lerp(next, t)
  }

  tangentAt(distance: number, out = new Vector3()): Vector3 {
    const { i } = this.locate(distance)
    const a = this.points[i]
    const b = this.points[Math.min(i + 1, this.points.length - 1)]
    out.copy(b).sub(a)
    if (out.lengthSq() < 1e-9 && i > 0) out.copy(this.points[i]).sub(this.points[i - 1])
    return out.lengthSq() < 1e-9 ? out.set(0, 0, 1) : out.normalize()
  }
}
